var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useEffect, useRef, useState } from 'react';
import { snakeCase } from 'lodash';
import Node from './node/Node';
import NodeGroup from './node/NodeGroup';
import ReactResizeDetector from 'react-resize-detector';
import LinkContainer from './link/LinkContainer';
import { DIAGRAM_COLUMN_CLASSNAME, DIAGRAM_CONTAINER_ID, DiagramObjectType } from './types';
import { arrangeNodesIntoColumns } from './logic/arrangeColumns';
import { useExpandNodesLogic } from './logic/expandNodes';
import { useHoverNodesLogic } from './logic/hoverNodes';
import { useNodeDomPositions } from './logic/nodeDomPositions';
import { PdfExportOverlay } from '../export/PdfExportOverlay';
import { horizontalDiagramToPDF } from '../export/horizontalDiagramToPDF';
import AccurityIconButton from '../../buttons/components/AccurityIconButton';
import classNames from 'classnames';
import { useHiddenLinksLogic } from './logic/hideLinks';
var HorizontalTreeDiagram = function (_a) {
    var nodes = _a.nodes, startNodeId = _a.startNodeId, numColumns = _a.numColumns, onCloseClick = _a.onCloseClick, _b = _a.nodesCollapsibleFromColumn, nodesCollapsibleFromColumn = _b === void 0 ? 1 : _b, _c = _a.nodesExpandedFromColumn, nodesExpandedFromColumn = _c === void 0 ? nodesCollapsibleFromColumn : _c, _d = _a.filterableLinkTypes, filterableLinkTypes = _d === void 0 ? [] : _d;
    var _e = useState([]), columns = _e[0], setColumns = _e[1];
    var diagramExporterRef = useRef();
    // manage expanded + unlinked state
    var _f = useExpandNodesLogic(nodes, columns, startNodeId, nodesCollapsibleFromColumn, nodesExpandedFromColumn), expandedNodeIds = _f.expandedNodeIds, isNodeExpandable = _f.isNodeExpandable, isNodeExpanded = _f.isNodeExpanded, isNodeUnlinked = _f.isNodeUnlinked, onExpandNodeToggle = _f.onExpandNodeToggle;
    // manage hover + highlight/dimmed state
    var _g = useHoverNodesLogic(nodes, columns, expandedNodeIds), highlightedNodeIds = _g.highlightedNodeIds, getNodeHoverState = _g.getNodeHoverState, onHoverNodeStart = _g.onHoverNodeStart, onHoverNodeStop = _g.onHoverNodeStop;
    // manage link hiding state
    var _h = useHiddenLinksLogic(filterableLinkTypes), toggleComponent = _h.toggleComponent, hiddenLinkTypes = _h.hiddenLinkTypes;
    // organise columns according to expanded and hidden state
    useEffect(function () {
        setColumns(arrangeNodesIntoColumns(nodes, expandedNodeIds, hiddenLinkTypes, startNodeId, numColumns));
    }, [expandedNodeIds, hiddenLinkTypes]);
    // manage node positions in DOM to allow rendering links
    var _j = useNodeDomPositions(nodes, columns), onContainerResize = _j.onContainerResize, nodePositions = _j.nodePositions;
    // render
    var renderNodeRecursively = function (node, columnIndex) {
        var NodeComponent = node.type === DiagramObjectType.GROUP ? NodeGroup : Node;
        return (React.createElement(NodeComponent, __assign({}, node, { key: node.id, isMini: DiagramObjectType.MINI_NODE === node.type, expandable: isNodeExpandable(node, columnIndex), expanded: isNodeExpanded(node.id), hoverState: getNodeHoverState(node.id), unlinked: isNodeUnlinked(node.id, columnIndex), onExpandToggle: function () { return onExpandNodeToggle(node.id); }, onHoverStart: function () { return onHoverNodeStart(node, columnIndex); }, onHoverStop: function () { return onHoverNodeStop(node, columnIndex); } }), nodes
            .filter(function (childNode) { return columns[columnIndex].includes(childNode.id); })
            .filter(function (childNode) { return childNode.groupParentNode === node.id; })
            .map(function (childNode) { return renderNodeRecursively(childNode, columnIndex); })));
    };
    var renderColumns = function () {
        var renderedColumns = [];
        var _loop_1 = function (index) {
            var columnNodes = nodes.filter(function (node) { var _a; return (_a = columns[index]) === null || _a === void 0 ? void 0 : _a.includes(node.id); });
            var allNodesMini = columnNodes.every(function (node) { return DiagramObjectType.MINI_NODE === node.type; });
            if (columnNodes.length > 0) {
                renderedColumns.push((React.createElement("div", { key: "column-" + index, className: classNames(DIAGRAM_COLUMN_CLASSNAME, { 'diagram__column--mini': allNodesMini }) },
                    React.createElement("div", { className: "diagram__columnVerticalCenterer" }),
                    columnNodes
                        .filter(function (node) { return node && !node.groupParentNode; })
                        .map(function (node) { return node && renderNodeRecursively(node, index); }),
                    React.createElement("div", { className: "diagram__columnVerticalCenterer" }))));
            }
        };
        for (var index = 0; index < numColumns; index++) {
            _loop_1(index);
        }
        return renderedColumns;
    };
    var handleDownloadToPdf = function () {
        if (diagramExporterRef.current) {
            var startNode = nodes.find(function (q) { return q.id === startNodeId; });
            var filename_1 = snakeCase((startNode === null || startNode === void 0 ? void 0 : startNode.label) + "_dld");
            var exportFunction = function () { return horizontalDiagramToPDF(document.querySelector(".diagram__content"), filename_1); };
            diagramExporterRef.current.export(exportFunction);
        }
    };
    return (React.createElement("div", { className: "diagram__container" },
        React.createElement("div", { className: "diagram__topBar" },
            React.createElement("div", { className: "diagram_topButtons" },
                toggleComponent,
                React.createElement(AccurityIconButton, { iconName: "mui:CloudDownloadOutlined", size: "small", "aria-label": "diagram__downloadToPdf", title: "Export to PDF", onClick: handleDownloadToPdf }),
                React.createElement(AccurityIconButton, { iconName: "mui:Close", size: "small", "aria-label": "diagram__closeButton", onClick: onCloseClick }))),
        React.createElement(ReactResizeDetector, { handleWidth: true, handleHeight: true, onResize: onContainerResize },
            React.createElement(PdfExportOverlay, { ref: diagramExporterRef, constructStyle: function (isAutoHeight) { return ({ height: isAutoHeight ? 'unset' : 'calc(100% - 40px)' }); } },
                React.createElement("div", { className: "diagram__content", id: DIAGRAM_CONTAINER_ID },
                    renderColumns(),
                    React.createElement(LinkContainer, { nodes: nodes, nodePositions: nodePositions, highlightedNodeIds: highlightedNodeIds, expandedNodeIds: expandedNodeIds, hiddenLinkTypes: hiddenLinkTypes }))))));
};
export default HorizontalTreeDiagram;
//# sourceMappingURL=HorizontalTreeDiagram.js.map